import type { TableLayout } from "@kaleidoscope-solutions/engine-wasm"
import type { FunctionalComponent } from "vue"
import {
  BestSellersIcon,
  BlockIcon,
  ChannelsIcon,
  DashboardIcon,
  DataHealthIcon,
  LineChartIcon,
  LocationIcon,
  PageIcon,
  ProductLineIcon,
  ProductsIcon,
  SalesIcon,
  SettingIcon,
  StockHealthIcon,
  StockIcon,
  TableLayoutIcon
} from "@kaleidoscope-solutions/ks-icons/vue/light"
import { PhaseSteps } from "@/types"

// TODO(Matt) - temporary file to wrap some of the hardcoded names used around the platform during alpha
// https://github.com/kaleidoscope-solutions/ks-ui-web/issues/489

// Alpha Projects
export const ALPHA_PROJECT_DETAIL_PAGES = "_detail_pages"
export const ALPHA_PROJECT_MANAGE_DATA = "Manage data"

// Alpha Pages
export const ALPHA_PAGE_OVERVIEW = "Overview"
export const ALPHA_PAGE_BEST_SELLERS = "Best sellers"
export const ALPHA_PAGE_RANGE_REVIEW = "Range review"
export const ALPHA_PAGE_CHANNEL = "Channel"
export const ALPHA_PAGE_PRODUCT_LINE = "Product line"
export const ALPHA_PAGE_PRODUCT = "Product"
export const ALPHA_PAGE_PRODUCTS = "Products"
export const ALPHA_PAGE_CHANNELS = "Channels"
export const ALPHA_PAGE_PRODUCT_ANALYSIS = "Product analysis"
export const ALPHA_PAGE_CHANNEL_ANALYSIS = "Channel analysis"
export const ALPHA_PAGE_MAKE_YOUR_OWN = "Make your own"
export const ALPHA_PAGE_LOCATIONS = "Locations"
export const ALPHA_PAGE_STOCK = "Stock"
export const ALPHA_PAGE_ALL_STOCK = "All stock"
export const ALPHA_PAGE_STOCK_FORECAST = "Stock forecast"
export const ALPHA_PAGE_DATA_HEALTH = "Data health"
export const ALPHA_PAGE_STOCK_HEALTH = "Stock health"
export const ALPHA_PAGE_ACTUAL = "Actual"
export const ALPHA_PAGE_FORECAST = "Forecast"
export const ALPHA_PAGE_STOCK_OVERVIEW = "Stock overview"
export const ALPHA_PAGE_STOCK_ANALYSIS = "Stock analysis"
export const ALPHA_PAGE_YOY_TRENDS = "YOY Trends"

// Temporary Page to show blocks
export const ALPHA_PAGE_BLOCKS = "Blocks"

export const EXTERNAL_NAME_SUFFIX = "::External Name"

export const ALPHA_PRODUCTS = "Products"
export const ALPHA_PRODUCT = "Product"
export const ALPHA_CHANNELS = "Channels"
export const ALPHA_CHANNEL = "Channel"
export const ALPHA_SALES = "Sales"
export const ALPHA_VARIANT_ID = "Selling item"
export const ALPHA_QUANTITY_SOLD = "Quantity sold"
export const ALPHA_UNIT_PRICE = "Unit Price"
export const ALPHA_DATE = "Date"

export const ALPHA_ATTR_PRODUCT = "Product"
export const ALPHA_ATTR_LOCATION = "Location"
export const ALPHA_ATTR_SIZE = "Size"
export const ALPHA_ATTR_COLOR = "Color"
export const ALPHA_ATTR_STYLE = "Style"
export const ALPHA_ATTR_PRODUCT_LINE = "Product line"
export const ALPHA_WEIGHTING_COLUMN = "Weighting Column"
export const ALPHA_SETTINGS = "Settings"

// Bundles
export const ALPHA_COMPONENT_QUANTITY = "Component quantity"
export const ALPHA_COMPONENT = "Component"
export const ALPHA_SELLING_ITEM_TABLE = "Selling items"

export const ALPHA_PRODUCTS_ATTRS = [
  ALPHA_ATTR_PRODUCT,
  ALPHA_ATTR_SIZE,
  ALPHA_ATTR_COLOR,
  ALPHA_ATTR_STYLE,
  ALPHA_ATTR_PRODUCT_LINE,
]

export const ALPHA_CHANNEL_CATEGORY = "Channel category"

export const FORECAST_TABLES = [
  "quantity sold - monthly",
  "quantity sold - weekly",
  "product - sales over time",
  "product - business value detail",
  "product - best selling",
  "combination parameters",
  "forecast adjustment detail",
  "stock overview - order now",
  "stock overview - overdue",
  "stock overview - order later",
  "stock analysis detail",
  "stock - business value detail",
  "stock balance monthly",
]

export const ALPHA_APP_TOOLTIP = new Map<string, string>([
  [
    ALPHA_QUANTITY_SOLD,
    `It is the amount of each Selling item you sold. 
  It can be named differently, depending on the file.`,
  ],
  [
    "Channel",
    `It's where the file comes from. 
  For example, if your file comes from Amazon, the channel will be Amazon
  `,
  ],
  [
    ALPHA_VARIANT_ID,
    `<span>A <strong>Selling item</strong> refers to the unique product item that is sold in this channel.
Different channels may have different names for your selling items.</span>`,
  ],
  [
    ALPHA_DATE,
    `It's the date your Selling item were sold.
  It can be named differently, depending on the file.`,
  ],
  [
    ALPHA_WEIGHTING_COLUMN,
    `Column to weight by`
  ]
])

export const FILE_IMPORT_BANNERS = new Map<PhaseSteps, string>([
  [
    PhaseSteps.SELECT,
    `Information about **Quantity sold**, **Selling item** and **Date** will enable you to perform analysis of your sales across your Channels.

  Tell Kaleidoscope where to find this information by **selecting the corresponding column from the file** (or simply review our guesses if any).`,
  ],
])

export function getPageIcon(pageName: string): FunctionalComponent {
  const lowerPageName = pageName.toLowerCase()

  const pageIconMap: Record<string, FunctionalComponent> = {
    [ALPHA_PAGE_BEST_SELLERS.toLowerCase()]: BestSellersIcon,
    [ALPHA_PAGE_BLOCKS.toLowerCase()]: BlockIcon,
    [ALPHA_PAGE_OVERVIEW.toLowerCase()]: DashboardIcon,
    [ALPHA_PAGE_RANGE_REVIEW.toLowerCase()]: LineChartIcon,
    [ALPHA_PRODUCTS.toLowerCase()]: ProductsIcon,
    [ALPHA_PRODUCT.toLowerCase()]: ProductsIcon,
    [ALPHA_PAGE_PRODUCT_ANALYSIS.toLowerCase()]: ProductsIcon,
    [ALPHA_ATTR_PRODUCT_LINE.toLowerCase()]: ProductLineIcon,
    [ALPHA_CHANNELS.toLowerCase()]: ChannelsIcon,
    [ALPHA_CHANNEL.toLowerCase()]: ChannelsIcon,
    [ALPHA_PAGE_CHANNEL_ANALYSIS.toLowerCase()]: ChannelsIcon,
    [ALPHA_SALES.toLowerCase()]: SalesIcon,
    [ALPHA_SETTINGS.toLowerCase()]: SettingIcon,
    [ALPHA_PAGE_LOCATIONS.toLowerCase()]: LocationIcon,
    [ALPHA_PAGE_STOCK.toLowerCase()]: StockIcon,
    [ALPHA_PAGE_ALL_STOCK.toLowerCase()]: StockIcon,
    [ALPHA_PAGE_STOCK_FORECAST.toLowerCase()]: LineChartIcon,
    [ALPHA_PAGE_DATA_HEALTH.toLowerCase()]: DataHealthIcon,
    [ALPHA_PAGE_STOCK_HEALTH.toLowerCase()]: StockHealthIcon,
    [ALPHA_PAGE_MAKE_YOUR_OWN.toLowerCase()]: TableLayoutIcon,
    [ALPHA_PAGE_MAKE_YOUR_OWN.toLowerCase()]: TableLayoutIcon,
    [ALPHA_PAGE_FORECAST.toLowerCase()]: LineChartIcon,
    [ALPHA_PAGE_STOCK_OVERVIEW.toLowerCase()]: DashboardIcon,
    [ALPHA_PAGE_STOCK_ANALYSIS.toLowerCase()]: StockHealthIcon,
    [ALPHA_PAGE_YOY_TRENDS.toLowerCase()]: DashboardIcon,
  }

  return pageIconMap[lowerPageName] ?? PageIcon
}

export function parseListData(listData: string) {
  return listData?.split("\n").slice(1, -1) || []
}

// We currently have to infer the target to import into from the table layout.
// We should switch this to be defined in the table definition to be explicit
// in which list we want the to import into from a table
export function getTargetFromTableLayout(layout: TableLayout) {
  let target = layout.rowElements[0].name

  if (target.endsWith("::All")) {
    target = target.replace("::All", "")
  } else if (target.endsWith("::External Names")) {
    target = target.replace("::External Names", "::External Name")
  }
  return target
}
